/**
 * TeamsDrawer — team card with W/L record, next game countdown, roster grid.
 * Theme-aware: uses Tailwind dark: variants throughout.
 * Uses getTeamStyle() from teamColors.js for team color rendering.
 * Shows team logo prominently when available.
 * Differentiates logged-out vs logged-in-but-no-team states.
 */
import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import {
  Users,
  ChevronRight,
  Clock,
  MapPin,
  Trophy,
  UserCircle2,
  ClipboardList,
  LogIn,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getTeamStyle } from "../../utils/teamColors";
import DrawerShell from "./DrawerShell";
import CroppedAvatar from "../CroppedAvatar";

// ── Countdown helper ──────────────────────────────────────────────────────────
const useCountdown = (targetDateStr) => {
  const [label, setLabel] = useState("");

  useEffect(() => {
    if (!targetDateStr) return;
    const update = () => {
      const target = new Date(`${targetDateStr}`);
      const diff = target - Date.now();
      if (diff <= 0) {
        setLabel("Now");
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      if (h >= 48) setLabel(`${Math.floor(h / 24)}d away`);
      else if (h >= 1) setLabel(`${h}h ${m}m`);
      else setLabel(`${m}m`);
    };
    update();
    const id = setInterval(update, 30000);
    return () => clearInterval(id);
  }, [targetDateStr]);

  return label;
};

// ── Next game card ────────────────────────────────────────────────────────────
// games table does NOT have a game_time column — time display removed
const NextGameCard = ({ game, myTeamId }) => {
  const countdown = useCountdown(game?.game_date);
  if (!game) return null;

  const isTeam1 = game.team1?.id === myTeamId;
  const opponent = isTeam1 ? game.team2 : game.team1;
  const vsLabel = isTeam1 ? "vs" : "@";
  const venue = game.court || null;

  const dateStr = new Date(game.game_date).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return (
    <div
      className="mx-5 mt-4 rounded-2xl
                    bg-orange-50 dark:bg-orange-500/10
                    border border-orange-200 dark:border-orange-500/25
                    p-4"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-black tracking-[0.18em] uppercase text-orange-600 dark:text-orange-400">
          Next Game
        </span>
        <span
          className="flex items-center gap-1 text-xs font-bold
                         text-orange-600 dark:text-orange-300
                         bg-orange-100 dark:bg-orange-500/20
                         px-2 py-0.5 rounded-full"
        >
          <Clock size={10} />
          {countdown}
        </span>
      </div>
      <p className="text-base font-black text-gray-900 dark:text-white">
        {vsLabel} {opponent?.name || "TBD"}
      </p>
      <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
        <span>{dateStr}</span>
        {venue && (
          <span className="flex items-center gap-1">
            <MapPin size={10} />
            {venue}
          </span>
        )}
      </div>
    </div>
  );
};

// ── Roster avatar row ─────────────────────────────────────────────────────────
const RosterRow = ({ roster }) => {
  if (!roster?.length) return null;
  const visible = roster.slice(0, 8);
  const overflow = roster.length - visible.length;

  return (
    <div className="mx-5 mt-4">
      <p
        className="text-[10px] font-black tracking-[0.18em] uppercase
                    text-gray-400 dark:text-gray-500 mb-2.5"
      >
        Roster
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        {visible.map((player, i) => (
          <motion.div
            key={player.id || i}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              delay: i * 0.04,
              type: "spring",
              stiffness: 400,
              damping: 28,
            }}
            className="flex flex-col items-center gap-1"
          >
            <div className="relative flex-shrink-0">
              {player.profile?.avatar_url ? (
                <CroppedAvatar
                  avatarUrl={player.profile.avatar_url}
                  avatarCrop={player.profile.avatar_crop}
                  size={40}
                  className="border-2 border-gray-200 dark:border-gray-700"
                />
              ) : (
                <div
                  className="w-10 h-10 rounded-full
                                bg-gray-100 dark:bg-gray-800
                                border-2 border-gray-200 dark:border-gray-700
                                flex items-center justify-center"
                >
                  <UserCircle2
                    size={22}
                    className="text-gray-400 dark:text-gray-600"
                  />
                </div>
              )}
              {player.jersey_number != null && (
                <span className="absolute bottom-0 right-0 w-4 h-4 bg-orange-500 rounded-full text-[8px] font-black text-white flex items-center justify-center leading-none">
                  {player.jersey_number}
                </span>
              )}
            </div>
            <span className="text-[9px] text-gray-400 dark:text-gray-500 font-medium max-w-[40px] truncate text-center leading-tight">
              {(player.profile?.name || player.name || "—").split(" ")[0]}
            </span>
          </motion.div>
        ))}
        {overflow > 0 && (
          <div className="flex flex-col items-center gap-1">
            <div
              className="w-10 h-10 rounded-full
                            bg-gray-100 dark:bg-gray-800
                            border-2 border-gray-200 dark:border-gray-700
                            flex items-center justify-center"
            >
              <span className="text-xs font-bold text-gray-500 dark:text-gray-400">
                +{overflow}
              </span>
            </div>
            <span className="text-[9px] text-gray-400 dark:text-gray-600">
              more
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Team logo / color swatch ──────────────────────────────────────────────────
const TeamLogoSwatch = ({ team, teamStyle, size = 56 }) => {
  if (team?.logo_url) {
    return (
      <div
        className="flex-shrink-0 rounded-xl overflow-hidden shadow-lg border border-white/20 dark:border-white/10 bg-white dark:bg-gray-800"
        style={{ width: size, height: size }}
      >
        <img
          src={team.logo_url}
          alt={team.name}
          className="w-full h-full object-contain p-1"
        />
      </div>
    );
  }
  // Fallback: colored swatch with trophy icon
  return (
    <div
      className="flex-shrink-0 rounded-xl flex items-center justify-center shadow-lg"
      style={{
        width: size,
        height: size,
        ...(teamStyle?.headerStyle || { backgroundColor: "#f97316" }),
      }}
    >
      <Trophy
        size={Math.round(size * 0.38)}
        className="text-white drop-shadow"
      />
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const TeamsDrawer = ({ open, onClose, myTeam, isCaptain, isAdmin, user }) => {
  const navigate = useNavigate();

  const winPct =
    myTeam && myTeam.wins + myTeam.losses > 0
      ? ((myTeam.wins / (myTeam.wins + myTeam.losses)) * 100).toFixed(0)
      : "—";

  // Use getTeamStyle() from teamColors.js — handles # prefix and gradient
  const teamStyle = myTeam ? getTeamStyle(myTeam) : null;

  const handleNav = (path) => {
    onClose();
    navigate(path);
  };

  // ── Logged-out state ────────────────────────────────────────────────────────
  const renderLoggedOut = () => (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div
        className="w-16 h-16 rounded-2xl mb-4 flex items-center justify-center
                      bg-orange-50 dark:bg-orange-500/10
                      border border-orange-200 dark:border-orange-500/25"
      >
        <LogIn size={28} className="text-orange-500 dark:text-orange-400" />
      </div>
      <p className="text-gray-900 dark:text-white text-base font-black mb-1">
        Sign in to see your team
      </p>
      <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-5">
        Log in to view your active season team name, roster, record, and
        upcoming games.
      </p>
      <button
        onClick={() => handleNav("/login")}
        className="flex items-center gap-2 px-5 py-2.5 rounded-xl
                   bg-orange-500 hover:bg-orange-600
                   text-white text-sm font-bold
                   transition-colors active:scale-95"
      >
        <LogIn size={15} />
        Log In
      </button>
    </div>
  );

  // ── No team (logged in but not on a team) ───────────────────────────────────
  const renderNoTeam = () => (
    <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
      <Users size={40} className="text-gray-300 dark:text-gray-700 mb-3" />
      <p className="text-gray-500 dark:text-gray-500 text-sm font-medium">
        Not on a team this season.
      </p>
      <p className="text-gray-400 dark:text-gray-600 text-xs mt-1">
        Check back after registration opens.
      </p>
    </div>
  );

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      title="My Team"
      subtitle={
        myTeam?.name || (user ? "No team assigned" : "Sign in to continue")
      }
    >
      {!user ? (
        renderLoggedOut()
      ) : !myTeam ? (
        renderNoTeam()
      ) : (
        <div className="pb-4">
          {/* ── Team record card ─────────────────────────────────────────── */}
          <div
            className="mx-5 mt-4 rounded-2xl
                          bg-gray-50 dark:bg-gray-900
                          border border-gray-200 dark:border-white/[0.07]
                          p-4 flex items-center gap-4"
          >
            {/* Team logo or color swatch */}
            <TeamLogoSwatch team={myTeam} teamStyle={teamStyle} size={56} />

            <div className="flex-1 min-w-0">
              <p className="text-base font-black text-gray-900 dark:text-white truncate">
                {myTeam.name}
              </p>
              {/* W/L record */}
              <div className="flex items-center gap-3 mt-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-2xl font-black text-gray-900 dark:text-white tabular-nums">
                    {myTeam.wins ?? 0}
                  </span>
                  <span className="text-xl font-black text-gray-400 dark:text-gray-500 select-none px-0.5">
                    –
                  </span>
                  <span className="text-2xl font-black text-gray-900 dark:text-white tabular-nums">
                    {myTeam.losses ?? 0}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase tracking-wide">
                    Win %
                  </span>
                  <span className="text-sm font-black text-orange-500 dark:text-orange-400">
                    {winPct}%
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={() => handleNav(`/teams/${myTeam.id}`)}
              className="flex-shrink-0 w-9 h-9 rounded-full
                         bg-gray-100 dark:bg-white/[0.07]
                         hover:bg-gray-200 dark:hover:bg-white/[0.12]
                         flex items-center justify-center transition-colors"
            >
              <ChevronRight
                size={16}
                className="text-gray-400 dark:text-gray-400"
              />
            </button>
          </div>

          {/* Next game */}
          <NextGameCard game={myTeam.nextGame} myTeamId={myTeam.id} />

          {/* Roster */}
          <RosterRow roster={myTeam.roster} />

          {/* Captain shortcuts */}
          {(isCaptain || isAdmin) && (
            <div className="mx-5 mt-5 space-y-2">
              <p
                className="text-[10px] font-black tracking-[0.18em] uppercase
                            text-gray-400 dark:text-gray-500 mb-2"
              >
                Captain Actions
              </p>
              <button
                onClick={() => handleNav("/captain")}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl
                           bg-gray-50 dark:bg-gray-900
                           border border-gray-200 dark:border-white/[0.07]
                           hover:border-orange-400 dark:hover:border-orange-500/40
                           hover:bg-orange-50 dark:hover:bg-orange-500/5
                           transition-all group"
              >
                <ClipboardList
                  size={16}
                  className="text-orange-500 dark:text-orange-400 flex-shrink-0"
                />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                  Manage Lineup
                </span>
                <ChevronRight
                  size={14}
                  className="text-gray-300 dark:text-gray-600 ml-auto group-hover:text-orange-500 dark:group-hover:text-orange-400 transition-colors"
                />
              </button>
              <button
                onClick={() => handleNav("/captain")}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl
                           bg-gray-50 dark:bg-gray-900
                           border border-gray-200 dark:border-white/[0.07]
                           hover:border-orange-400 dark:hover:border-orange-500/40
                           hover:bg-orange-50 dark:hover:bg-orange-500/5
                           transition-all group"
              >
                <Users
                  size={16}
                  className="text-orange-500 dark:text-orange-400 flex-shrink-0"
                />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                  Full Roster
                </span>
                <ChevronRight
                  size={14}
                  className="text-gray-300 dark:text-gray-600 ml-auto group-hover:text-orange-500 dark:group-hover:text-orange-400 transition-colors"
                />
              </button>
            </div>
          )}

          {/* Admin shortcut */}
          {isAdmin && (
            <div className="mx-5 mt-2">
              <button
                onClick={() => handleNav("/admin?tab=teams")}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl
                           bg-red-50 dark:bg-gray-900
                           border border-red-200 dark:border-white/[0.07]
                           hover:border-red-400 dark:hover:border-red-500/40
                           hover:bg-red-100 dark:hover:bg-red-500/5
                           transition-all group"
              >
                <Trophy
                  size={16}
                  className="text-red-500 dark:text-red-400 flex-shrink-0"
                />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                  Manage All Teams
                </span>
                <ChevronRight
                  size={14}
                  className="text-gray-300 dark:text-gray-600 ml-auto group-hover:text-red-500 dark:group-hover:text-red-400 transition-colors"
                />
              </button>
            </div>
          )}
        </div>
      )}
    </DrawerShell>
  );
};

export default TeamsDrawer;
